"use client";

import { createPermissionChecker } from "@/helper/permissionsChecker";
import { useTranslation } from "@/lib/sajilni-localization";
import { useSession } from "@sajilni/auth/react";
import { Organization, WhiteLabel } from "database";
import { useWhiteLabel } from "@/context/white-label-context";
import { useHeaderAccentColor } from "@/hooks/use-header-accent-color";
import { getRoleName } from "@/helper/getRoleName";
import { getInitials } from "@sajilni/utils/getInitials";
import {
  ArrowLeftRight,
  Building2,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  ShoppingBag,
  type LucideIcon,
} from "lucide-react";
import { signOut } from "next-auth/react";
import { resetAnalytics } from "@/lib/analytics/identify";
import Link from "next/link";
import { useParams, useSelectedLayoutSegments } from "next/navigation";
import { useEffect, useState } from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "ui";
import { cn } from "@sajilni/utils/cn";
import { generateOrganizationUrl } from "@/helper/generateOrganizationUrl";
import { generateBuyerDashboardUrl } from "@/helper/generateBuyerDashboardUrl";

function UserAvatar({
  src,
  name,
  className,
}: {
  src?: string | null;
  name: string;
  className?: string;
}) {
  return (
    <Avatar className={className}>
      {src ? <AvatarImage src={src} alt={name} /> : null}
      <AvatarFallback className="text-xs font-medium text-gray-600">
        {getInitials(name).replaceAll(" ", "")}
      </AvatarFallback>
    </Avatar>
  );
}

function ProfileMenuLink({
  href,
  icon: Icon,
  children,
}: {
  href: string;
  icon: LucideIcon;
  children: string;
}) {
  return (
    <DropdownMenuItem asChild>
      <Link href={href} className="gap-2 whitespace-normal">
        <Icon className="size-4 shrink-0" />
        <span className="break-words">{children}</span>
      </Link>
    </DropdownMenuItem>
  );
}

export const UserProfileMenu = ({
  userName,
  level,
  isEventWallPage,
}: {
  userName?: string;
  showAllMenuItems?: boolean;
  isParticipantsPortalPage?: boolean;
  level?: "ADMIN" | "TEAM" | "SETUP" | null;
  isEventWallPage?: boolean;
}) => {
  const { slug } = useParams() as { slug: string };
  const { t } = useTranslation("sajilni2.0");
  const { data: session } = useSession();
  const isOrganizer = session?.roleV2Context?.hasOrganizerRole;
  const isSuperAdmin = session?.roleV2Context?.hasSuperAdminRole;
  const isWhiteLabelSuperAdmin =
    session?.roleV2Context?.hasWhiteLabelSuperAdminRole;
  const { currentEventNetworkingSettings, whiteLabel } = useWhiteLabel();
  const { hasEventAccent, textStyle } = useHeaderAccentColor();
  const role = getRoleName(session?.user?.roles?.[0])?.replaceAll("_", " ");
  const [organization, setOrganization] = useState<
    (Organization & { whiteLabel: WhiteLabel }) | null
  >(null);
  const organizerUrl = generateOrganizationUrl(
    organization?.slug ?? "",
    whiteLabel?.subdomain ?? organization?.whiteLabel?.subdomain ?? "",
    organization?.whiteLabel?.customDomain
  );

  const segments = useSelectedLayoutSegments();
  const subdomain =
    typeof window !== "undefined"
      ? window.location.hostname.split(".")[0]
      : undefined;
  const buyerDashboardUrl = generateBuyerDashboardUrl(
    whiteLabel?.subdomain ?? organization?.whiteLabel?.subdomain ?? "",
    subdomain
  );

  const isOrganizerDashboard = subdomain?.startsWith("admin-");
  const isParticpant = segments?.includes("(participants)");
  const checker = createPermissionChecker(session?.capabilities);
  const canAccessCompanyProfile = checker.canAccess(
    "exhibitorPermissions.companySettings"
  );
  const canAccessAdminPortal = checker.canAccess("boothAdmin");

  const isAdminDashboardPage =
    segments?.includes("admin") || subdomain === "admin";
  const isBuyerDashboardPage = segments?.includes("buyer-dashboard");

  const userWhiteLabelIds = [
    session?.whiteLabelId,
    session?.user?.whiteLabelId,
    session?.user?.accessibleWhiteLabelId,
    session?.roleV2Context?.whiteLabelId,
  ].filter((id): id is string => typeof id === "string" && id.length > 0);

  const hasCurrentWhiteLabelAccess =
    !!whiteLabel?.id && userWhiteLabelIds.includes(whiteLabel.id);

  const hasAdminRoles = (session?.roleV2Context?.roles?.length ?? 0) > 0;

  const canAccessAdminDashboard =
    isSuperAdmin ||
    isWhiteLabelSuperAdmin ||
    isOrganizer ||
    (hasAdminRoles && hasCurrentWhiteLabelAccess);

  const showNetworkingHub =
    (level === "ADMIN" || level === "SETUP") && !isEventWallPage && !!slug;
  const showCompanyProfile =
    (level === "ADMIN" || isParticpant || !!isEventWallPage) &&
    canAccessCompanyProfile &&
    !!slug;
  const showAdminSetup =
    (level === "SETUP" || isParticpant || !!isEventWallPage) &&
    !!currentEventNetworkingSettings?.switchToBoothAdmin &&
    canAccessAdminPortal &&
    !!slug;
  const showPortalSwitches =
    showNetworkingHub || showCompanyProfile || showAdminSetup;

  const showAdminDashboardLink =
    !isAdminDashboardPage && canAccessAdminDashboard;
  const showBuyerDashboardLink = !isBuyerDashboardPage;
  const showOrganizerDashboardLink = !isOrganizerDashboard && !!isOrganizer;
  const showDashboardLinks =
    showAdminDashboardLink ||
    showBuyerDashboardLink ||
    showOrganizerDashboardLink;

  useEffect(() => {
    const fetchOrganization = async () => {
      const response = await fetch(
        `/api/user/organization?whiteLabelId=${
          whiteLabel?.id ?? ""
        }&organizationslug=${slug ?? ""}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      setOrganization(data.organization);
    };
    if (isOrganizer) fetchOrganization();
  }, [slug, isOrganizer, whiteLabel]);

  if (!session?.user) return null;

  const displayName = userName ?? session.user.name ?? "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="group flex items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
        aria-label={displayName || "User menu"}
      >
        <UserAvatar
          src={session.user.image}
          name={displayName}
          className="!h-8 !w-8"
        />
        <ChevronDown className="size-3.5 text-gray-500 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-start gap-2.5">
            <UserAvatar
              src={session.user.image}
              name={displayName}
              className="!h-8 !w-8"
            />
            <div className="min-w-0 flex-1">
              <p className="break-words text-sm font-semibold leading-tight text-gray-900">
                {displayName}
              </p>
              {role ? (
                <p
                  className={cn(
                    "mt-0.5 break-words text-xs leading-snug",
                    !hasEventAccent && "text-primary"
                  )}
                  style={hasEventAccent ? textStyle : undefined}
                >
                  {role}
                </p>
              ) : null}
            </div>
          </div>
        </DropdownMenuLabel>
        {showPortalSwitches ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {showNetworkingHub ? (
                <ProfileMenuLink
                  href={`/event/${slug}/participants`}
                  icon={ArrowLeftRight}
                >
                  {t("profileMenu.switchToNetworkingHub")}
                </ProfileMenuLink>
              ) : null}
              {showCompanyProfile ? (
                <ProfileMenuLink
                  href={`/event/${slug}/participants-portal/setup-details`}
                  icon={ArrowLeftRight}
                >
                  {t("profileMenu.switchToCompanyProfile")}
                </ProfileMenuLink>
              ) : null}
              {showAdminSetup ? (
                <ProfileMenuLink
                  href={`/event/${slug}/participants-portal/details`}
                  icon={ArrowLeftRight}
                >
                  {t("profileMenu.switchToAdminSetup")}
                </ProfileMenuLink>
              ) : null}
            </DropdownMenuGroup>
          </>
        ) : null}
        {showDashboardLinks ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {showAdminDashboardLink ? (
                <ProfileMenuLink href="/admin" icon={LayoutDashboard}>
                  {t("profileMenu.adminDashboard")}
                </ProfileMenuLink>
              ) : null}
              {showBuyerDashboardLink ? (
                <ProfileMenuLink href={buyerDashboardUrl} icon={ShoppingBag}>
                  {t("profileMenu.buyerDashboard")}
                </ProfileMenuLink>
              ) : null}
              {showOrganizerDashboardLink ? (
                <ProfileMenuLink href={organizerUrl} icon={Building2}>
                  {t("profileMenu.organizerDashboard")}
                </ProfileMenuLink>
              ) : null}
            </DropdownMenuGroup>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            resetAnalytics();
            void signOut();
          }}
          className="cursor-pointer gap-2 text-destructive focus:text-destructive"
        >
          <LogOut className="size-4 shrink-0" />
          {t("Sign out")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
